<script setup lang="ts">
export interface SnackbarProps {
  modelValue?: boolean
  message?: string
  color?: string
  timeout?: number
  location?: 'top' | 'bottom' | 'left' | 'right' | 'center'
  multiLine?: boolean
  vertical?: boolean
  closable?: boolean
  icon?: string
  actionText?: string
  actionColor?: string
}

const props = withDefaults(defineProps<SnackbarProps>(), {
  modelValue: false,
  message: '',
  color: 'primary',
  timeout: 4000,
  location: 'bottom',
  multiLine: false,
  vertical: false,
  closable: true,
  icon: '',
  actionText: '',
  actionColor: 'white',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'action': []
  'close': []
}>()

// 반응형 상태
const isVisible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value),
})

// 컬러 매핑
const snackbarColor = computed(() => {
  const colorMap: Record<string, string> = {
    success: 'success',
    error: 'error',
    warning: 'warning',
    info: 'info',
  }
  return colorMap[props.color] || props.color
})

// 메서드
function close() {
  isVisible.value = false
  emit('close')
}

function onClose(value: boolean) {
  if (!value) {
    emit('close')
  }
}

function onAction() {
  emit('action')
}

// 외부에서 사용할 수 있는 메서드 노출
defineExpose({
  close,
})
</script>

<template>
  <v-snackbar
    v-model="isVisible" :color="snackbarColor" :timeout="timeout" :location="location"
    :multi-line="multiLine" :vertical="vertical" :closable="closable" @update:model-value="onClose"
  >
    <v-sheet class="d-flex align-center">
      <v-icon v-if="icon" :icon="icon" class="me-2" />
      <span>{{ message }}</span>
    </v-sheet>

    <template #actions>
      <v-btn v-if="actionText" variant="text" :color="actionColor" @click="onAction">
        {{ actionText }}
      </v-btn>
      <v-btn v-if="closable" variant="text" icon="mdi-close" @click="close" />
    </template>
  </v-snackbar>
</template>
